// src/modules/Investors/pages/InvestorDashboard.js
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../config/supabase';
import {
  FaChartLine,
  FaUsers,
  FaCheckCircle,
  FaPercentage,
  FaArrowUp,
  FaArrowDown,
  FaSpinner,
  FaCalendarAlt,
  FaSyncAlt,
  FaWallet,
  FaBullseye,
  FaReceipt,
  FaChartBar
} from 'react-icons/fa';

// Styled Components - Light Theme
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    color: #999;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #111;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    font-size: 12px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #666;
    font-size: 14px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 4}, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const MetricCard = styled(Card)`
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${props => props.$bg || '#f5f5f5'};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      font-size: 16px;
      color: ${props => props.$color || '#666'};
    }
  }

  .change {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.$trend === 'up' ? '#10b981' : props.$trend === 'down' ? '#ef4444' : '#666'};
  }

  .value {
    font-size: 28px;
    font-weight: 700;
    color: #111;
    margin-bottom: 2px;
  }

  .target {
    font-size: 12px;
    color: #999;
  }

  .label {
    font-size: 13px;
    color: #666;
    margin-top: 8px;
  }

  .progress {
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
    margin-top: 12px;
    overflow: hidden;

    .fill {
      height: 100%;
      border-radius: 2px;
      background: ${props => props.$color || '#111'};
      width: ${props => Math.min(props.$percent || 0, 100)}%;
      transition: width 500ms ease;
    }
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const GoalCard = styled(Card)`
  padding: 16px;

  .goal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .goal-title {
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }

  .goal-category {
    font-size: 11px;
    padding: 4px 8px;
    background: #f5f5f5;
    border-radius: 4px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .goal-values {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
  }

  .goal-current {
    font-size: 24px;
    font-weight: 700;
    color: #111;
  }

  .goal-target {
    font-size: 14px;
    color: #999;
  }

  .goal-progress {
    height: 6px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 6px;

    .fill {
      height: 100%;
      border-radius: 3px;
      background: ${props => {
        const pct = props.$percent || 0;
        if (pct >= 100) return '#10b981';
        if (pct >= 75) return '#3b82f6';
        if (pct >= 50) return '#f59e0b';
        return '#ef4444';
      }};
      width: ${props => Math.min(props.$percent || 0, 100)}%;
    }
  }

  .goal-percent {
    font-size: 12px;
    font-weight: 600;
    color: ${props => {
      const pct = props.$percent || 0;
      if (pct >= 100) return '#10b981';
      if (pct >= 75) return '#3b82f6';
      if (pct >= 50) return '#f59e0b';
      return '#ef4444';
    }};
  }
`;

const ExpenseRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  .expense-info {
    flex: 1;
  }

  .expense-category {
    font-size: 14px;
    font-weight: 500;
    color: #111;
    margin-bottom: 2px;
  }

  .expense-description {
    font-size: 12px;
    color: #999;
  }

  .expense-values {
    text-align: right;
  }

  .expense-actual {
    font-size: 15px;
    font-weight: 600;
    color: #111;
  }

  .expense-planned {
    font-size: 12px;
    color: #999;
  }

  .expense-variance {
    font-size: 11px;
    font-weight: 500;
    color: ${props => props.$variance > 0 ? '#ef4444' : '#10b981'};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
    margin-top: 2px;
  }
`;

const TotalsCard = styled(Card)`
  background: #111;
  color: white;

  .totals-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .total-item {
    text-align: center;
  }

  .total-label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 4px;
  }

  .total-value {
    font-size: 18px;
    font-weight: 700;
    color: white;
  }

  .total-variance {
    font-size: 12px;
    color: ${props => props.$positive ? '#10b981' : '#ef4444'};
    margin-top: 2px;
  }
`;

const FunnelStage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;

  .stage-name {
    width: 100px;
    font-size: 13px;
    color: #666;
  }

  .stage-bar {
    flex: 1;
    height: 24px;
    background: #f5f5f5;
    border-radius: 4px;
    overflow: hidden;
    position: relative;

    .fill {
      height: 100%;
      background: ${props => props.$color || '#3b82f6'};
      border-radius: 4px;
    }
  }

  .stage-value {
    width: 60px;
    text-align: right;
    font-size: 14px;
    font-weight: 600;
    color: #111;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: #666;

  svg {
    font-size: 24px;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #999;
  font-size: 14px;
`;

const FUNNEL_STAGES = [
  { key: 'new', label: 'Novos', color: '#3b82f6' },
  { key: 'contacted', label: 'Contatados', color: '#f59e0b' },
  { key: 'qualified', label: 'Qualificados', color: '#8b5cf6' },
  { key: 'proposal', label: 'Proposta', color: '#06b6d4' },
  { key: 'converted', label: 'Convertidos', color: '#10b981' }
];

const CATEGORY_NAMES = {
  marketing: 'Marketing',
  development: 'Desenvolvimento',
  operations: 'Operacoes',
  equipment: 'Equipamentos',
  personnel: 'Pessoal',
  infrastructure: 'Infraestrutura',
  other: 'Outros'
};

const InvestorDashboard = () => {
  const { investorData } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [currentGoal, setCurrentGoal] = useState(null);
  const [investorGoals, setInvestorGoals] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseTotals, setExpenseTotals] = useState({ planned: 0, actual: 0 });
  const [funnelData, setFunnelData] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchMetrics(),
      fetchCurrentGoal(),
      fetchInvestorGoals(),
      fetchExpenses(),
      fetchFunnelData()
    ]);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const fetchMetrics = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const { data, error } = await supabase.rpc('get_period_metrics', {
        p_start_date: startOfMonth,
        p_end_date: endOfMonth
      });

      if (!error && data && data.length > 0) {
        setMetrics(data[0]);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  };

  const fetchCurrentGoal = async () => {
    try {
      const { data, error } = await supabase.rpc('get_current_goals', {
        p_period_type: 'monthly'
      });

      if (!error && data && data.length > 0) {
        setCurrentGoal(data[0]);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  };

  const fetchInvestorGoals = async () => {
    try {
      const { data, error } = await supabase
        .from('investor_goals')
        .select('*')
        .eq('status', 'in_progress')
        .order('created_at', { ascending: false });

      if (!error) {
        setInvestorGoals(data || []);
      }
    } catch (error) {
      console.warn('Investor goals table may not exist:', error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      const { data, error } = await supabase
        .from('expense_declarations')
        .select('*')
        .gte('period_start', startDate)
        .lte('period_end', endDate)
        .order('category', { ascending: true });

      if (!error) {
        setExpenses(data || []);

        const totals = (data || []).reduce((acc, exp) => ({
          planned: acc.planned + (parseFloat(exp.planned_amount) || 0),
          actual: acc.actual + (parseFloat(exp.actual_amount) || 0)
        }), { planned: 0, actual: 0 });

        setExpenseTotals(totals);
      }
    } catch (error) {
      console.warn('Expenses table may not exist:', error);
    }
  };

  const fetchFunnelData = async () => {
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('status');

      if (error) throw error;

      const counts = {};
      for (const lead of data || []) {
        counts[lead.status] = (counts[lead.status] || 0) + 1;
      }

      const total = data?.length || 0;
      const funnel = FUNNEL_STAGES.map(stage => ({
        ...stage,
        count: counts[stage.key] || 0,
        percent: total > 0 ? Math.round(((counts[stage.key] || 0) / total) * 100) : 0
      }));

      setFunnelData(funnel);
    } catch (error) {
      console.error('Error fetching funnel:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const calculatePercent = (current, target) => {
    if (!target || target === 0) return 0;
    return Math.round((current / target) * 100);
  };

  if (loading) {
    return (
      <LoadingOverlay>
        <FaSpinner />
        Carregando dados...
      </LoadingOverlay>
    );
  }

  const leadsPercent = calculatePercent(metrics?.total_leads, currentGoal?.leads_target);
  const qualifiedPercent = calculatePercent(metrics?.qualified_leads, currentGoal?.qualified_leads_target);
  const conversionsPercent = calculatePercent(metrics?.converted_leads, currentGoal?.deals_target);
  const convRatePercent = calculatePercent(metrics?.conversion_rate, currentGoal?.conversion_rate_target);

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderLeft>
          <Title>Ola, {investorData?.name?.split(' ')[0] || 'Investidor'}</Title>
          <Subtitle>
            <FaCalendarAlt />
            Atualizado em {new Date().toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Subtitle>
        </HeaderLeft>
        <RefreshButton onClick={handleRefresh} disabled={refreshing}>
          <FaSyncAlt style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </RefreshButton>
      </Header>

      {/* KPIs de Prospecção */}
      <SectionTitle>
        <FaChartLine />
        Desempenho do Mes
      </SectionTitle>
      <Grid $cols={4}>
        <MetricCard $color="#3b82f6" $bg="rgba(59, 130, 246, 0.1)" $percent={leadsPercent} $trend={leadsPercent >= 100 ? 'up' : leadsPercent >= 50 ? 'neutral' : 'down'}>
          <div className="header">
            <div className="icon"><FaUsers /></div>
            <div className="change">
              {leadsPercent >= 100 ? <FaArrowUp /> : <FaArrowDown />}
              {leadsPercent}%
            </div>
          </div>
          <div className="value">{metrics?.total_leads || 0}</div>
          <div className="target">Meta: {currentGoal?.leads_target || '-'}</div>
          <div className="label">Leads Captados</div>
          <div className="progress"><div className="fill" /></div>
        </MetricCard>

        <MetricCard $color="#8b5cf6" $bg="rgba(139, 92, 246, 0.1)" $percent={qualifiedPercent}>
          <div className="header">
            <div className="icon"><FaCheckCircle /></div>
            <div className="change">
              {qualifiedPercent >= 100 ? <FaArrowUp /> : <FaArrowDown />}
              {qualifiedPercent}%
            </div>
          </div>
          <div className="value">{metrics?.qualified_leads || 0}</div>
          <div className="target">Meta: {currentGoal?.qualified_leads_target || '-'}</div>
          <div className="label">Qualificados</div>
          <div className="progress"><div className="fill" /></div>
        </MetricCard>

        <MetricCard $color="#10b981" $bg="rgba(16, 185, 129, 0.1)" $percent={conversionsPercent}>
          <div className="header">
            <div className="icon"><FaWallet /></div>
            <div className="change">
              {conversionsPercent >= 100 ? <FaArrowUp /> : <FaArrowDown />}
              {conversionsPercent}%
            </div>
          </div>
          <div className="value">{metrics?.converted_leads || 0}</div>
          <div className="target">Meta: {currentGoal?.deals_target || '-'}</div>
          <div className="label">Conversoes</div>
          <div className="progress"><div className="fill" /></div>
        </MetricCard>

        <MetricCard $color="#f59e0b" $bg="rgba(245, 158, 11, 0.1)" $percent={convRatePercent}>
          <div className="header">
            <div className="icon"><FaPercentage /></div>
            <div className="change">
              {convRatePercent >= 100 ? <FaArrowUp /> : <FaArrowDown />}
              {convRatePercent}%
            </div>
          </div>
          <div className="value">{metrics?.conversion_rate || 0}%</div>
          <div className="target">Meta: {currentGoal?.conversion_rate_target || '-'}%</div>
          <div className="label">Taxa de Conversao</div>
          <div className="progress"><div className="fill" /></div>
        </MetricCard>
      </Grid>

      {/* Metas com Investidores + Funil */}
      <TwoColumnGrid>
        <div>
          <SectionTitle>
            <FaBullseye />
            Metas Acordadas
          </SectionTitle>
          {investorGoals.length === 0 ? (
            <Card>
              <EmptyState>Nenhuma meta cadastrada</EmptyState>
            </Card>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {investorGoals.slice(0, 4).map((goal) => {
                const percent = goal.target_value > 0 ? (goal.current_value / goal.target_value) * 100 : 0;
                return (
                  <GoalCard key={goal.id} $percent={percent}>
                    <div className="goal-header">
                      <span className="goal-title">{goal.title}</span>
                      <span className="goal-category">{goal.category}</span>
                    </div>
                    <div className="goal-values">
                      <span className="goal-current">
                        {goal.unit === 'R$' ? formatCurrency(goal.current_value) : `${goal.current_value}${goal.unit}`}
                      </span>
                      <span className="goal-target">
                        / {goal.unit === 'R$' ? formatCurrency(goal.target_value) : `${goal.target_value}${goal.unit}`}
                      </span>
                    </div>
                    <div className="goal-progress"><div className="fill" /></div>
                    <span className="goal-percent">{percent.toFixed(0)}% concluido</span>
                  </GoalCard>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <SectionTitle>
            <FaChartBar />
            Funil de Vendas
          </SectionTitle>
          <Card>
            {funnelData.map((stage) => {
              const maxCount = Math.max(...funnelData.map(s => s.count), 1);
              const barWidth = (stage.count / maxCount) * 100;
              return (
                <FunnelStage key={stage.key} $color={stage.color}>
                  <span className="stage-name">{stage.label}</span>
                  <div className="stage-bar">
                    <div className="fill" style={{ width: `${barWidth}%` }} />
                  </div>
                  <span className="stage-value">{stage.count}</span>
                </FunnelStage>
              );
            })}
          </Card>
        </div>
      </TwoColumnGrid>

      {/* Declaração de Gastos */}
      <SectionTitle>
        <FaReceipt />
        Declaracao de Gastos ({new Date().getFullYear()})
      </SectionTitle>

      <TotalsCard $positive={expenseTotals.actual <= expenseTotals.planned}>
        <div className="totals-grid">
          <div className="total-item">
            <div className="total-label">Planejado</div>
            <div className="total-value">{formatCurrency(expenseTotals.planned)}</div>
          </div>
          <div className="total-item">
            <div className="total-label">Realizado</div>
            <div className="total-value">{formatCurrency(expenseTotals.actual)}</div>
          </div>
          <div className="total-item">
            <div className="total-label">Variacao</div>
            <div className="total-value">{formatCurrency(expenseTotals.actual - expenseTotals.planned)}</div>
            <div className="total-variance">
              {expenseTotals.planned > 0 ? (
                <>
                  {expenseTotals.actual <= expenseTotals.planned ? <FaArrowDown /> : <FaArrowUp />}
                  {Math.abs(((expenseTotals.actual - expenseTotals.planned) / expenseTotals.planned) * 100).toFixed(0)}%
                </>
              ) : '-'}
            </div>
          </div>
        </div>
      </TotalsCard>

      <Card style={{ marginTop: 16 }}>
        {expenses.length === 0 ? (
          <EmptyState>Nenhuma despesa declarada</EmptyState>
        ) : (
          expenses.map((expense) => {
            const variance = (parseFloat(expense.actual_amount) || 0) - (parseFloat(expense.planned_amount) || 0);
            return (
              <ExpenseRow key={expense.id} $variance={variance}>
                <div className="expense-info">
                  <div className="expense-category">{CATEGORY_NAMES[expense.category] || expense.category}</div>
                  <div className="expense-description">{expense.description}</div>
                </div>
                <div className="expense-values">
                  <div className="expense-actual">{formatCurrency(expense.actual_amount)}</div>
                  <div className="expense-planned">de {formatCurrency(expense.planned_amount)}</div>
                  {variance !== 0 && (
                    <div className="expense-variance">
                      {variance > 0 ? <FaArrowUp /> : <FaArrowDown />}
                      {formatCurrency(Math.abs(variance))}
                    </div>
                  )}
                </div>
              </ExpenseRow>
            );
          })
        )}
      </Card>
    </Container>
  );
};

export default InvestorDashboard;
